import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  Rocket, LayoutDashboard, Inbox as InboxIcon, Calendar, Users, TrendingUp,
  CreditCard, Sparkles, Bot, Send, Workflow, Globe, Award, HardDrive, Star,
  BarChart3, Grid, Settings as Cog, ChevronDown, ChevronRight, ChevronLeft,
  LogOut, User as UserIcon, Search, Phone, HelpCircle,
  Megaphone, Plus, MapPin, Zap, Gift, Check, ShieldCheck, CheckCircle2,
  Pencil, Edit3, Lock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import AIAssistant from "@/components/AIAssistant";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import AppGuideTour from "@/components/AppGuideTour";
import PlatformGuideChatbot from "@/components/PlatformGuideChatbot";
import TrialExpiredLockView from "@/components/TrialExpiredLockView";
import { useAuth } from "@/context/AuthContext";
import { useLocationContext } from "@/context/LocationContext";
import { toast } from "sonner";

// ── GoHighLevel Navigation Taxonomy ──
const GHL_PRIMARY_NAV = [
  { to: "/launchpad", label: "Launchpad", icon: Rocket, testid: "nav-launchpad" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/inbox", label: "Conversations", icon: InboxIcon, testid: "nav-inbox" },
  { to: "/calendars", label: "Calendars", icon: Calendar, testid: "nav-calendars" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/pipeline", label: "Opportunities", icon: TrendingUp, testid: "nav-pipeline" },
  { to: "/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
];

const GHL_GROWTH_NAV = [
  { to: "/ai-studio", label: "AI Studio", icon: Sparkles, badge: "Beta", testid: "nav-ai-studio" },
  { to: "/ai-agents", label: "AI Agents", icon: Bot, testid: "nav-ai-agents" },
  { to: "/marketing", label: "Marketing", icon: Send, testid: "nav-marketing" },
  { to: "/automations", label: "Automation", icon: Workflow, testid: "nav-automations" },
  { to: "/sites", label: "Sites", icon: Globe, testid: "nav-sites" },
  { to: "/memberships", label: "Memberships", icon: Award, testid: "nav-memberships" },
  { to: "/media", label: "Media Storage", icon: HardDrive, testid: "nav-media" },
  { to: "/reputation", label: "Reputation", icon: Star, testid: "nav-reputation" },
  { to: "/referrals", label: "Referrals", icon: Gift, badge: "25%", testid: "nav-referrals" },
  { to: "/reporting", label: "Reporting", icon: BarChart3, testid: "nav-reporting" },
  { to: "/marketplace", label: "App Marketplace", icon: Grid, testid: "nav-marketplace" },
];

// Section top navigation tabs matching GoHighLevel screenshots exactly
const SECTION_TABS = {
  "/inbox": {
    title: "Conversations",
    defaultTab: "conversations",
    tabs: [
      { id: "conversations", label: "Conversations" },
      { id: "manual-actions", label: "Manual Actions" },
      { id: "snippets", label: "Snippets" },
      { id: "trigger-links", label: "Trigger Links" },
      { id: "analytics", label: "Analytics" },
      { id: "settings", label: "Settings" },
    ],
  },
  "/ai-agents": {
    title: "Autonomous AI Agents",
    defaultTab: "orchestrator",
    tabs: [
      { id: "orchestrator", label: "Scenario Creator" },
      { id: "site-architect", label: "Site Architect" },
      { id: "course-creator", label: "LLM Course Creator" },
      { id: "reputation-agent", label: "Reputation Maximizer" },
    ],
  },
  "/referrals": {
    title: "Referral Program",
    defaultTab: "overview",
    tabs: [
      { id: "overview", label: "Overview & Link" },
      { id: "partners", label: "Referred Clients" },
      { id: "marketing-kit", label: "Marketing Kit" },
      { id: "payouts", label: "Payout Settings" },
    ],
  },
  "/marketing": {
    title: "Marketing",
    defaultTab: "social-planner",
    tabs: [
      { id: "social-planner", label: "Social Planner" },
      { id: "emails", label: "Emails" },
      { id: "snippets", label: "Snippets" },
      { id: "countdown-timers", label: "Countdown Timers" },
      { id: "trigger-links", label: "Trigger Links" },
      { id: "affiliate-manager", label: "Affiliate Manager", hasDropdown: true },
      { id: "brand-boards", label: "Brand Boards" },
      { id: "ad-manager", label: "Ad Manager" },
    ],
  },
  "/payments": {
    title: "Payments",
    defaultTab: "products",
    tabs: [
      { id: "invoices", label: "Invoices & Estimates", hasDropdown: true },
      { id: "documents", label: "Documents & Contracts", hasDropdown: true },
      { id: "orders", label: "Orders", hasDropdown: true },
      { id: "subscriptions", label: "Subscriptions" },
      { id: "payment-links", label: "Payment Links" },
      { id: "transactions", label: "Transactions" },
      { id: "products", label: "Products", hasDropdown: true },
      { id: "coupons", label: "Coupons" },
      { id: "gift-cards", label: "Gift Cards" },
      { id: "settings", label: "Settings" },
      { id: "integrations", label: "Integrations" },
    ],
  },
  "/reputation": {
    title: "Reputation",
    defaultTab: "overview",
    tabs: [
      { id: "overview", label: "Overview" },
      { id: "requests", label: "Requests" },
      { id: "reviews", label: "Reviews" },
      { id: "testimonials", label: "Video Testimonials", badge: "New" },
      { id: "widgets", label: "Widgets" },
      { id: "listings", label: "Listings" },
      { id: "gbp", label: "GBP Optimization" },
      { id: "settings", label: "Settings" },
    ],
  },
  "/memberships": {
    title: "Memberships",
    defaultTab: "client-portal",
    tabs: [
      { id: "client-portal", label: "Client Portal", hasDropdown: true },
      { id: "courses", label: "Courses", hasDropdown: true },
      { id: "communities", label: "Communities", hasDropdown: true },
      { id: "credentials", label: "Credentials", hasDropdown: true },
      { id: "marketplace", label: "Gokollab Marketplace", badge: "New" },
    ],
  },
  "/sites": {
    title: "Sites",
    defaultTab: "funnels",
    tabs: [
      { id: "funnels", label: "Funnels" },
      { id: "websites", label: "Websites" },
      { id: "stores", label: "Stores" },
      { id: "webinars", label: "Webinars" },
      { id: "analytics", label: "Analytics" },
      { id: "blogs", label: "Blogs" },
      { id: "wordpress", label: "WordPress" },
      { id: "client-portal", label: "Client Portal", hasDropdown: true },
      { id: "forms", label: "Forms" },
      { id: "surveys", label: "Surveys" },
      { id: "quizzes", label: "Quizzes" },
      { id: "chat", label: "Chat Widget" },
      { id: "qr", label: "QR Codes" },
    ],
  },
};

export default function Layout() {
  const [aiOpen, setAiOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneDial, setPhoneDial] = useState("");
  const [calling, setCalling] = useState(false);
  const [twilioInfo, setTwilioInfo] = useState({ connected: false, phone_number: "" });
  const [searchVal, setSearchVal] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const { locations, activeLocation, switchLocation, createLocation, updateLocation } = useLocationContext();
  const [createLocSidebarOpen, setCreateLocSidebarOpen] = useState(false);
  const [sidebarLocForm, setSidebarLocForm] = useState({
    name: "",
    city: "",
    state: "",
    category: "Commercial Photography & Media",
    address: "",
    phone: "",
  });

  const [editLocModalOpen, setEditLocModalOpen] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [editLocForm, setEditLocForm] = useState({
    name: "",
    city: "",
    state: "",
    category: "Commercial Photography & Media",
    address: "",
    phone: "",
  });

  const handleOpenEditLocation = (locToEdit) => {
    const target = locToEdit || activeLocation;
    setEditingLoc(target);
    setEditLocForm({
      name: target.name || "",
      city: target.city || "",
      state: target.state || "",
      category: target.category || "Commercial Photography & Media",
      address: target.address || "",
      phone: target.phone || "",
    });
    setEditLocModalOpen(true);
  };

  const handleSaveEditLocation = async (e) => {
    e.preventDefault();
    if (!editLocForm.name || !editLocForm.city || !editLocForm.state) {
      toast.error("Location name, city, and state are required");
      return;
    }
    try {
      await updateLocation(editingLoc?.id || activeLocation.id, editLocForm);
      setEditLocModalOpen(false);
    } catch {}
  };

  const loc = useLocation();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signout } = useAuth();
  const [showTour, setShowTour] = useState(false);
  const [helpChatbotOpen, setHelpChatbotOpen] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState({ percent: 100, isNew: false, isComplete: true });

  const isMasterUser =
    user?.role === "master" ||
    user?.role === "admin" ||
    ["colibrisocialclub@gmail.com", "contact@amanbiswalthakur.in", "thyzfoto@gmail.com", "admin@weddingsthyzfoto.com"].includes(user?.email?.toLowerCase());

  const userHasPaidPlan =
    isMasterUser ||
    user?.plan === "pro" ||
    user?.plan === "scale" ||
    user?.plan === "agency" ||
    (user?.subscription?.status === "active" && !["free", "trial"].includes(user?.subscription?.plan_id));

  // Determine if 7-day trial is active or expired
  const trialEnds = user?.trial_ends_at || user?.subscription?.trial_ends_at;
  const isTrialActive = Boolean(
    (user?.is_trial || user?.subscription?.is_trial) &&
    trialEnds &&
    new Date(trialEnds).getTime() > Date.now()
  );

  const isTrialExpired = Boolean(
    !isMasterUser &&
    !userHasPaidPlan &&
    (
      (trialEnds && new Date(trialEnds).getTime() <= Date.now()) ||
      (!isTrialActive && !userHasPaidPlan)
    )
  );

  const [upgradePlanModalOpen, setUpgradePlanModalOpen] = useState(false);

  const handleCreateSidebarLoc = async (e) => {
    e.preventDefault();
    if (!sidebarLocForm.name || !sidebarLocForm.city || !sidebarLocForm.state) {
      toast.error("Location name, city, and state are required");
      return;
    }
    try {
      await createLocation(sidebarLocForm);
      setCreateLocSidebarOpen(false);
      setSidebarLocForm({ name: "", city: "", state: "", category: "Commercial Photography & Media", address: "", phone: "" });
    } catch (err) {
      if (err?.response?.status === 402) {
        setCreateLocSidebarOpen(false);
        setUpgradePlanModalOpen(true);
      }
    }
  };

  // Dynamic Workspace Logo & Name
  const [customLogo, setCustomLogo] = useState(() => localStorage.getItem("ld_workspace_logo") || user?.logo_url || null);
  const [workspaceName, setWorkspaceName] = useState(() => localStorage.getItem("ld_workspace_name") || user?.business_name || "LaunchDeck");

  useEffect(() => {
    const handleBranding = () => {
      setCustomLogo(localStorage.getItem("ld_workspace_logo") || user?.logo_url || null);
      setWorkspaceName(localStorage.getItem("ld_workspace_name") || user?.business_name || "LaunchDeck");
    };
    window.addEventListener("workspace_branding_updated", handleBranding);
    window.addEventListener("storage", handleBranding);
    return () => {
      window.removeEventListener("workspace_branding_updated", handleBranding);
      window.removeEventListener("storage", handleBranding);
    };
  }, [user]);

  useEffect(() => {
    // Check if new account for automatic tour - scoped by user ID
    if (!user) return;
    const tourKey = `launchdeck_app_tour_done_${user.id}`;
    const tourDone = localStorage.getItem(tourKey) || localStorage.getItem("launchdeck_app_tour_done");
    const createdAt = user?.created_at ? new Date(user.created_at).getTime() : 0;
    const isNew = createdAt > 0 ? (Date.now() - createdAt < 10 * 60 * 1000) : false;

    if (!tourDone && (isNew || localStorage.getItem("launchdeck_is_new_user") === "true")) {
      setShowTour(true);
    }

    // Calculate profile completeness
    let points = 0;
    const total = 4;
    if (user?.name || user?.business_name) points++;
    if (user?.phone || activeLocation?.phone) points++;
    if (activeLocation?.address || activeLocation?.city) points++;
    if (twilioInfo.connected || user?.stripe_connected) points++;

    const percent = Math.round((points / total) * 100);
    const launchpadDone = user?.id
      ? localStorage.getItem(`launchpad_profile_completed_${user.id}`) === "true"
      : false;
    const isComplete = percent >= 100 || launchpadDone || localStorage.getItem("launchpad_profile_completed") === "true";

    setProfileCompletion({ percent, isNew, isComplete });
  }, [user, activeLocation, twilioInfo]);

  useEffect(() => {
    api.get("/integrations").then(({ data }) => {
      if (data?.twilio) {
        setTwilioInfo({
          connected: !!data.twilio.connected,
          phone_number: data.twilio.phone_number || "",
        });
      }
    }).catch(() => {});
  }, [phoneOpen]);

  // Current active section configuration
  const currentPathKey = Object.keys(SECTION_TABS).find(p => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  const currentSection = currentPathKey ? SECTION_TABS[currentPathKey] : null;
  const activeTabParam = searchParams.get("tab") || currentSection?.defaultTab || "";

  const initials = (user?.name || user?.email || "LN")
    .split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  const doSignout = async () => { await signout(); nav("/signin"); };

  const handleDial = (num) => {
    setPhoneDial(prev => prev + num);
  };

  const startCall = async (e) => {
    e.preventDefault();
    if (!phoneDial) {
      toast.error("Please enter a destination phone number to call");
      return;
    }
    if (!twilioInfo.connected) {
      toast.error("Twilio is not connected. Please connect Twilio in App Marketplace to enable phone calling.");
      return;
    }
    setCalling(true);
    try {
      const { data } = await api.post("/integrations/call", { to_phone: phoneDial });
      toast.success(`Call placed via Twilio! Call SID: ${data.call_sid}`);
      setPhoneOpen(false);
      setPhoneDial("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Twilio call failed");
    } finally {
      setCalling(false);
    }
  };

  const handleSelectTab = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>

      {/* ── GoHighLevel Left Sidebar (Exact Match to User Screenshots) ── */}
      <aside
        data-testid="sidebar"
        style={{
          background: "#111827",
          borderRight: "1px solid #1f2937",
          width: collapsed ? 68 : 220,
        }}
        className="flex flex-col sticky top-0 h-screen shrink-0 transition-all duration-200 z-30 select-none relative"
      >
        {/* Top: Logo & Location Switcher */}
        <div className="p-3 border-b border-gray-800 space-y-2.5">
          {/* Logo iHUB / Custom Logo */}
          <div className="flex items-center gap-2 px-1">
            {customLogo ? (
              <img
                src={customLogo}
                alt="Workspace Logo"
                className="w-8 h-8 rounded-lg object-cover bg-white border border-gray-700 shadow-xs shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-black border border-yellow-500/40 flex items-center justify-center font-black text-amber-400 text-xs tracking-tight shadow-xs shrink-0">
                iHUB
              </div>
            )}
            {!collapsed && (
              <span className="font-extrabold text-sm tracking-wide text-white font-heading truncate">
                {workspaceName}
              </span>
            )}
          </div>

          {/* Location / Sub-Account Display (Dropdown for Master/Agency; Clean Badge for Sub-Account) */}
          {isMasterUser || locations.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="location-switcher-trigger"
                  className={`w-full flex items-center gap-2 p-1.5 rounded-lg text-left transition-colors bg-gray-800/60 hover:bg-gray-800 border border-gray-700/60 cursor-pointer ${
                    collapsed ? "justify-center p-1" : ""
                  }`}
                  title={`${activeLocation.name} · ${activeLocation.city || "Primary Studio"}`}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-white text-[10px] shrink-0 bg-blue-600">
                    {activeLocation.name?.slice(0, 2).toUpperCase() || "ST"}
                  </div>
                  {!collapsed && (
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[11px] truncate text-white leading-tight">
                        {activeLocation.name}
                      </div>
                      <div className="text-[10px] truncate text-slate-400 leading-tight">
                        {activeLocation.city && activeLocation.state
                          ? `${activeLocation.city}, ${activeLocation.state}`
                          : (activeLocation.city || activeLocation.state || user?.email || "Studio Sub-Account")}
                      </div>
                    </div>
                  )}
                  {!collapsed && <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-gray-900 border-gray-800 text-white">
                <DropdownMenuLabel className="text-[10px] uppercase text-slate-400">Switch Location / Sub-Account</DropdownMenuLabel>
                {locations.map((locItem) => {
                  const isActive = activeLocation.id === locItem.id;
                  return (
                    <DropdownMenuItem
                      key={locItem.id}
                      className={`focus:bg-gray-800 cursor-pointer flex items-center justify-between ${
                        isActive ? "bg-gray-800/80 font-bold" : ""
                      }`}
                      onClick={() => switchLocation(locItem.id)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs font-semibold truncate">{locItem.name}</span>
                          <span className="text-[10px] text-slate-400 truncate">
                            {locItem.city && locItem.state
                              ? `${locItem.city}, ${locItem.state}`
                              : (locItem.city || locItem.state || "HQ Location")}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {isMasterUser && (
                          <button
                            type="button"
                            data-testid={`edit-loc-${locItem.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditLocation(locItem);
                            }}
                            className="p-1 rounded hover:bg-gray-700 text-slate-400 hover:text-amber-300 transition-colors"
                            title={`Edit ${locItem.name}`}
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        {isActive && <Check className="w-3.5 h-3.5 text-blue-400" />}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
                {isMasterUser && (
                  <>
                    <DropdownMenuSeparator className="bg-gray-800" />
                    <DropdownMenuItem
                      data-testid="edit-current-location-btn"
                      className="focus:bg-gray-800 cursor-pointer text-amber-400 font-medium text-xs flex items-center gap-2"
                      onClick={() => handleOpenEditLocation(activeLocation)}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit Active Sub-Account ({activeLocation.name})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="create-new-location-btn"
                      className="focus:bg-gray-800 cursor-pointer text-blue-400 font-medium"
                      onClick={() => {
                        if (!userHasPaidPlan && locations.length >= 1) {
                          setUpgradePlanModalOpen(true);
                        } else {
                          setCreateLocSidebarOpen(true);
                        }
                      }}
                    >
                      <Plus className="w-3.5 h-3.5 mr-2" /> Add New Sub-Account
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div
              className={`w-full flex items-center gap-2 p-1.5 rounded-lg text-left bg-gray-800/60 border border-gray-700/60 ${
                collapsed ? "justify-center p-1" : ""
              }`}
              title={`${activeLocation.name} · Sub-Account`}
            >
              <div className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-white text-[10px] shrink-0 bg-blue-600">
                {activeLocation.name?.slice(0, 2).toUpperCase() || "ST"}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[11px] truncate text-white leading-tight">
                    {activeLocation.name}
                  </div>
                  <div className="text-[10px] truncate text-slate-400 leading-tight">
                    {activeLocation.city && activeLocation.state
                      ? `${activeLocation.city}, ${activeLocation.state}`
                      : (activeLocation.category || "Sub-Account Studio")}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dialog to Create Location from Sidebar */}
          <Dialog open={createLocSidebarOpen} onOpenChange={setCreateLocSidebarOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span>Create New Location / Sub-Account</span>
                </DialogTitle>
                <DialogDescription>
                  Add a new location or sub-account to your studio workspace with isolated data and pipelines.
                </DialogDescription>
              </DialogHeader>

              {isMasterUser && (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2.5 text-xs text-amber-300">
                  <span className="text-base">👑</span>
                  <div>
                    <span className="font-bold text-amber-200">Master Panel VIP License:</span> Unlimited locations and sub-accounts unlocked at 100% complimentary free tier.
                  </div>
                </div>
              )}
              <form onSubmit={handleCreateSidebarLoc} className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Location / Studio Name</Label>
                  <Input
                    value={sidebarLocForm.name}
                    onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, name: e.target.value })}
                    placeholder="e.g. Colibri Studio Austin or RealHUB Miami"
                    className="mt-1 text-xs"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">City</Label>
                    <Input
                      value={sidebarLocForm.city}
                      onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, city: e.target.value })}
                      placeholder="e.g. Austin"
                      className="mt-1 text-xs"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">State / Region</Label>
                    <Input
                      value={sidebarLocForm.state}
                      onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, state: e.target.value })}
                      placeholder="e.g. Texas"
                      className="mt-1 text-xs"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Business Category</Label>
                  <select
                    value={
                      ["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(sidebarLocForm.category)
                        ? sidebarLocForm.category
                        : "Other / Custom Category"
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "Other / Custom Category") {
                        setSidebarLocForm({ ...sidebarLocForm, category: "Other" });
                      } else {
                        setSidebarLocForm({ ...sidebarLocForm, category: val });
                      }
                    }}
                    className="w-full mt-1 h-9 rounded-md border px-3 text-xs bg-transparent"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                  >
                    <option value="Commercial Photography & Media">Commercial Photography & Media</option>
                    <option value="Full-Service Marketing Agency">Full-Service Marketing Agency</option>
                    <option value="Hospitality & Event Venue">Hospitality & Event Venue</option>
                    <option value="Local Real Estate & Construction">Local Real Estate & Construction</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="E-Commerce & Retail">E-Commerce & Retail</option>
                    <option value="Technology & Software / SaaS">Technology & Software / SaaS</option>
                    <option value="Education & Coaching">Education & Coaching</option>
                    <option value="Other / Custom Category">Other / Custom Category</option>
                  </select>
                  {(!["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(sidebarLocForm.category) || sidebarLocForm.category.startsWith("Other")) && (
                    <div className="mt-2">
                      <Label className="text-[11px] font-semibold text-slate-400">Specify Custom / Other Category</Label>
                      <Input
                        value={sidebarLocForm.category === "Other" ? "" : sidebarLocForm.category}
                        onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, category: e.target.value || "Other" })}
                        placeholder="e.g. Wedding Photography, Consulting..."
                        className="mt-1 text-xs"
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Street Address</Label>
                    <Input
                      value={sidebarLocForm.address}
                      onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, address: e.target.value })}
                      placeholder="e.g. 201 Colorado St"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Phone</Label>
                    <Input
                      value={sidebarLocForm.phone}
                      onChange={(e) => setSidebarLocForm({ ...sidebarLocForm, phone: e.target.value })}
                      placeholder="e.g. +1 (512) 555-0144"
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>
                <DialogFooter className="pt-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setCreateLocSidebarOpen(false)}>Cancel</Button>
                  <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                    Create Location
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Paywall Modal for Location / Sub-account Creation */}
          <Dialog open={upgradePlanModalOpen} onOpenChange={setUpgradePlanModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 text-blue-500 flex items-center justify-center font-bold text-2xl mb-1">
                  🏢
                </div>
                <DialogTitle className="text-xl">Agency Plan Required for Sub-Accounts</DialogTitle>
                <DialogDescription className="text-sm pt-1 text-slate-400">
                  Creating additional physical locations or client sub-accounts requires an active Agency plan (Pro or Scale). Upgrade your workspace plan to unlock unlimited locations, isolated pipelines, and team sub-accounts.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2.5 py-3 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Deploy isolated pipelines and sub-account workspaces</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Assign distinct phone numbers, calendars, and domains</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Complimentary lifetime access for Master Panel accounts</span>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setUpgradePlanModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setUpgradePlanModalOpen(false);
                    nav("/billing");
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                >
                  View Plans & Upgrade
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog to Edit Sub-Account Location */}
          <Dialog open={editLocModalOpen} onOpenChange={setEditLocModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-amber-500" />
                  <span>Edit Sub-Account Location</span>
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-400">
                  Update this sub-account's name and physical address. Changes here only apply to this sub-account and will not modify your main provider brand ({workspaceName}).
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSaveEditLocation} className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Sub-Account / Location Name</Label>
                  <Input
                    data-testid="edit-loc-name-input"
                    value={editLocForm.name}
                    onChange={(e) => setEditLocForm({ ...editLocForm, name: e.target.value })}
                    placeholder="e.g. RealHUB or Colibri Austin"
                    className="mt-1 text-xs"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">City</Label>
                    <Input
                      data-testid="edit-loc-city-input"
                      value={editLocForm.city}
                      onChange={(e) => setEditLocForm({ ...editLocForm, city: e.target.value })}
                      placeholder="e.g. Middletown"
                      className="mt-1 text-xs"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">State / Region</Label>
                    <Input
                      data-testid="edit-loc-state-input"
                      value={editLocForm.state}
                      onChange={(e) => setEditLocForm({ ...editLocForm, state: e.target.value })}
                      placeholder="e.g. Delaware"
                      className="mt-1 text-xs"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Business Category</Label>
                  <select
                    value={
                      ["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(editLocForm.category)
                        ? editLocForm.category
                        : "Other / Custom Category"
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "Other / Custom Category") {
                        setEditLocForm({ ...editLocForm, category: "Other" });
                      } else {
                        setEditLocForm({ ...editLocForm, category: val });
                      }
                    }}
                    className="w-full mt-1 h-9 rounded-md border px-3 text-xs bg-transparent"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                  >
                    <option value="Commercial Photography & Media">Commercial Photography & Media</option>
                    <option value="Full-Service Marketing Agency">Full-Service Marketing Agency</option>
                    <option value="Hospitality & Event Venue">Hospitality & Event Venue</option>
                    <option value="Local Real Estate & Construction">Local Real Estate & Construction</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="E-Commerce & Retail">E-Commerce & Retail</option>
                    <option value="Technology & Software / SaaS">Technology & Software / SaaS</option>
                    <option value="Education & Coaching">Education & Coaching</option>
                    <option value="Other / Custom Category">Other / Custom Category</option>
                  </select>
                  {(!["Commercial Photography & Media", "Full-Service Marketing Agency", "Hospitality & Event Venue", "Local Real Estate & Construction", "Professional Services", "E-Commerce & Retail", "Technology & Software / SaaS", "Education & Coaching"].includes(editLocForm.category) || editLocForm.category.startsWith("Other")) && (
                    <div className="mt-2">
                      <Label className="text-[11px] font-semibold text-slate-400">Specify Custom / Other Category</Label>
                      <Input
                        value={editLocForm.category === "Other" ? "" : editLocForm.category}
                        onChange={(e) => setEditLocForm({ ...editLocForm, category: e.target.value || "Other" })}
                        placeholder="e.g. Wedding Photography, Consulting..."
                        className="mt-1 text-xs"
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Street Address</Label>
                    <Input
                      data-testid="edit-loc-address-input"
                      value={editLocForm.address}
                      onChange={(e) => setEditLocForm({ ...editLocForm, address: e.target.value })}
                      placeholder="e.g. 651 N Broad St, Suite 206"
                      className="mt-1 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase text-slate-500">Phone</Label>
                    <Input
                      data-testid="edit-loc-phone-input"
                      value={editLocForm.phone}
                      onChange={(e) => setEditLocForm({ ...editLocForm, phone: e.target.value })}
                      placeholder="e.g. +1 (302) 555-0199"
                      className="mt-1 text-xs"
                    />
                  </div>
                </div>
                <DialogFooter className="pt-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setEditLocModalOpen(false)}>Cancel</Button>
                  <Button data-testid="save-edit-loc-btn" type="submit" size="sm" className="bg-amber-600 hover:bg-amber-700 text-white font-semibold">
                    Save Sub-Account Changes
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Quick Search with green lightning bolt icon */}
          {!collapsed && (
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  placeholder="Search"
                  className="w-full h-7 pl-7 pr-8 rounded-md bg-gray-800/80 text-white text-[11px] border border-gray-700 focus:outline-hidden focus:border-blue-500"
                />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-sans text-slate-400 bg-gray-700/60 px-1 rounded">
                  ⌘K
                </span>
              </div>
              <button
                onClick={() => setAiOpen(true)}
                title="Quick AI Action"
                className="w-7 h-7 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/40 flex items-center justify-center shrink-0 transition-colors"
              >
                <Zap className="w-3.5 h-3.5 fill-emerald-400" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Items (Matching GHL Screenshot Sidebar) */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5 scrollbar-none text-xs">
          {/* CRM Core & Pipelines (Matching Screenshot) */}
          {GHL_PRIMARY_NAV.map(({ to, label, icon: Icon, badge, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              title={label}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  collapsed ? "justify-center px-1" : ""
                } ${
                  isTrialExpired ? "opacity-35 hover:opacity-60 transition-opacity" : ""
                } ${
                  isActive
                    ? "bg-[#242e42] text-white font-semibold"
                    : "text-slate-300 hover:bg-gray-800/50 hover:text-white"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {!collapsed && <span className="flex-1 truncate">{label}</span>}
              {!collapsed && isTrialExpired && (
                <Lock className="w-3 h-3 text-slate-500 shrink-0 ml-auto" />
              )}
              {!collapsed && !isTrialExpired && badge && (
                <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-blue-500/30 text-blue-200">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}

          <div className="my-1.5 border-t border-gray-800/80" />

          {/* Growth & Studio Suite */}
          {GHL_GROWTH_NAV.map(({ to, label, icon: Icon, badge, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              title={label}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  collapsed ? "justify-center px-1" : ""
                } ${
                  isTrialExpired ? "opacity-35 hover:opacity-60 transition-opacity" : ""
                } ${
                  isActive
                    ? "bg-[#242e42] text-white font-semibold"
                    : "text-slate-300 hover:bg-gray-800/60 hover:text-white"
                }`
              }
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {!collapsed && <span className="flex-1 truncate">{label}</span>}
              {!collapsed && isTrialExpired && (
                <Lock className="w-3 h-3 text-slate-500 shrink-0 ml-auto" />
              )}
              {!collapsed && !isTrialExpired && badge && (
                <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-400 text-slate-950 leading-none">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}

          <div className="my-1.5 border-t border-gray-800/80" />

          {/* Studio Suite Bridge */}
          <a
            href="https://google.weddingsthyzfoto.com"
            target="_blank"
            rel="noopener noreferrer"
            title="Open Studio"
            className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-400 hover:bg-emerald-950/30 border border-emerald-500/20 ${
              collapsed ? "justify-center px-1" : ""
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" />
            {!collapsed && <span className="truncate">Studio</span>}
          </a>

          {/* Unlock Workspace Link when Trial is Expired */}
          {isTrialExpired && (
            <NavLink
              to="/billing"
              data-testid="nav-billing-unlock"
              title="Unlock Workspace · Choose Plan"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-xs font-bold transition-all my-1.5 ${
                  collapsed ? "justify-center px-1" : ""
                } ${
                  isActive
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm"
                }`
              }
            >
              <CreditCard className="w-3.5 h-3.5 shrink-0 text-white" />
              {!collapsed && <span className="flex-1 truncate">Unlock Workspace</span>}
              {!collapsed && (
                <span className="px-1.5 py-0.2 rounded-full text-[9px] font-black bg-white text-blue-700 leading-none">
                  Plans
                </span>
              )}
            </NavLink>
          )}

          {/* Settings */}
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            title="Settings"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                collapsed ? "justify-center px-1" : ""
              } ${
                isTrialExpired ? "opacity-35 hover:opacity-60 transition-opacity" : ""
              } ${
                isActive
                  ? "bg-[#242e42] text-white font-semibold"
                  : "text-slate-300 hover:bg-gray-800/50 hover:text-white"
              }`
            }
          >
            <Cog className="w-3.5 h-3.5 shrink-0" />
            {!collapsed && <span className="truncate">Settings</span>}
            {!collapsed && isTrialExpired && (
              <Lock className="w-3 h-3 text-slate-500 shrink-0 ml-auto" />
            )}
          </NavLink>

          {/* Master Admin Portal link (visible to master/admin roles) */}
          {["master", "admin"].includes(user?.role) && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              title="Admin Portal"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  collapsed ? "justify-center px-1" : ""
                } ${
                  isActive
                    ? "bg-purple-900/60 text-purple-200 border border-purple-500/40 font-semibold"
                    : "text-purple-300 hover:bg-purple-950/40 hover:text-white"
                }`
              }
            >
              <ShieldCheck className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              {!collapsed && <span className="truncate">Admin Portal</span>}
              {!collapsed && (
                <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-500 text-white leading-none">
                  Master
                </span>
              )}
            </NavLink>
          )}
        </nav>

        {/* Sidebar Collapse Toggle Button with animated opposite rotation */}
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label="Toggle sidebar collapse"
          data-testid="sidebar-toggle-btn"
          className="absolute -right-3 bottom-6 w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center justify-center text-xs font-bold transition-all duration-300 shadow-md z-40 hover:scale-110 active:scale-95"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft
            className={`w-3.5 h-3.5 transition-transform duration-300 ease-in-out ${
              collapsed ? "rotate-180" : "rotate-0"
            }`}
          />
        </button>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <ImpersonationBanner />

        {/* ── Global Topbar Header (Unified with Section Tabs Matching GHL) ── */}
        <header
          className="h-14 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20 backdrop-blur-sm border-b"
          style={{
            background: "var(--surface)",
            borderColor: "var(--border)",
          }}
        >
          {/* Left: Section Title + Primary Horizontal Tabs (GHL Style) */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none flex-1 min-w-0">
            {currentSection ? (
              <div className="flex items-center gap-1 min-w-max">
                <span className="font-bold text-base mr-3" style={{ color: "var(--text)" }}>
                  {currentSection.title}
                </span>
                {currentSection.tabs.map((tab) => {
                  const active = activeTabParam === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleSelectTab(tab.id)}
                      data-testid={`top-tab-${tab.id}`}
                      className={`px-3 py-3.5 text-xs font-medium border-b-2 transition-all flex items-center gap-1 whitespace-nowrap ${
                        active
                          ? "border-blue-600 text-blue-600 font-semibold"
                          : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                      }`}
                    >
                      <span>{tab.label}</span>
                      {tab.hasDropdown && <ChevronDown className="w-3 h-3 text-slate-400" />}
                      {tab.badge && (
                        <span className="px-1 py-0.2 rounded text-[9px] font-bold bg-amber-400 text-slate-900 leading-none">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
                {currentPathKey === "/sites" && (
                  <button
                    onClick={() => nav("/settings?tab=domains")}
                    className="p-1.5 ml-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    title="Sites Settings"
                  >
                    <Cog className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-base" style={{ color: "var(--text)" }}>
                  {loc.pathname === "/" ? "Dashboard" : loc.pathname.slice(1).replace("-", " ").toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Right: Header Action Tools (Matching Exact GHL Colors & Icons) */}
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {/* Apple-Inspired Complete Profile Pill */}
            <Link to="/launchpad" className="apple-btn-wrapper hidden sm:inline-flex" title="Complete Profile Setup">
              <div className="apple-btn-inner !py-1 !px-3.5 !text-xs !gap-2">
                <span className="apple-badge-dot" />
                <span className="apple-shimmer-text-bright">Complete Profile</span>
              </div>
            </Link>

            {/* Quick Phone Dialer (Green Circle) */}
            <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
              <DialogTrigger asChild>
                <button
                  className="w-7 h-7 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-xs transition-transform hover:scale-105"
                  title="Omnichannel Phone Dialer"
                >
                  <Phone className="w-3.5 h-3.5" />
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xs">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-emerald-500" />
                    <span>Omnichannel Phone Dialer</span>
                  </DialogTitle>
                  <DialogDescription>
                    {twilioInfo.connected
                      ? `Connected via Twilio (${twilioInfo.phone_number})`
                      : "Twilio integration required for outbound calling."}
                  </DialogDescription>
                </DialogHeader>

                {!twilioInfo.connected ? (
                  <div className="py-2 space-y-3">
                    <div className="rounded-lg p-3 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60 text-amber-800 dark:text-amber-300 text-xs space-y-2">
                      <div className="font-bold">⚠️ Twilio Not Connected</div>
                      <p>Connect your Twilio Account SID and phone number in App Marketplace to enable live dialing.</p>
                    </div>
                    <Link to="/marketplace" onClick={() => setPhoneOpen(false)}>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-8">
                        Configure Twilio →
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={startCall} className="space-y-4 py-2">
                    <div className="text-[11px] text-emerald-600 font-medium">
                      Caller ID: {twilioInfo.phone_number}
                    </div>
                    <Input
                      type="tel"
                      value={phoneDial}
                      onChange={(e) => setPhoneDial(e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="text-center text-lg font-sans tracking-wider h-10"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, "*", 0, "#"].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => handleDial(n.toString())}
                          className="h-9 rounded-lg border font-bold text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
                          style={{ borderColor: "var(--border)" }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setPhoneDial("")}>Clear</Button>
                      <Button type="submit" size="sm" disabled={calling} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> {calling ? "Dialing…" : "Start Call"}
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* AI Studio Assistant (Purple Circle) */}
            <button
              onClick={() => setAiOpen(true)}
              data-testid="topbar-ai-btn"
              className="w-7 h-7 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center shadow-xs transition-transform hover:scale-105"
              title="Launch AI Studio"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>

            {/* Megaphone / Announcements (Teal Circle) */}
            <button
              onClick={() => toast.info("No system broadcasts at this time")}
              className="w-7 h-7 rounded-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center shadow-xs transition-transform hover:scale-105"
              title="Broadcasts"
            >
              <Megaphone className="w-3.5 h-3.5" />
            </button>

            {/* Notification Bell (Orange Circle) */}
            <NotificationBell />

            {/* 7-Day Free Trial Badge */}
            {user?.is_trial && (
              <Link
                to="/billing"
                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 transition-colors"
                title="Your 7-Day Free Trial is Active · View Plans"
              >
                <Sparkles className="w-3 h-3 text-blue-600" />
                <span>7-Day Trial</span>
              </Link>
            )}

            {/* Dark & Light Theme Switcher (Beside Bell) */}
            <ThemeToggle />

            {/* Colibri Studio Suite Button */}
            <a
              href="https://google.weddingsthyzfoto.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-[#0D281E] hover:bg-[#163E30] transition-colors shadow-xs"
              title="Open Colibri Studio"
            >
              <Sparkles className="w-3 h-3 text-amber-300" />
              <span>Studio</span>
            </a>

            {/* Help Question Mark (Blue Circle) - Directly opens Platform Guide Chatbot */}
            <button
              onClick={() => setHelpChatbotOpen(true)}
              className="w-7 h-7 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center shadow-xs transition-transform hover:scale-105 cursor-pointer"
              title="Platform Guide & AI Assistant"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>

            {/* User Profile Menu (Purple Circle LN) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="profile-menu-trigger"
                  className="w-7 h-7 rounded-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center text-[11px] font-bold shadow-xs cursor-pointer"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <span className="text-sm font-semibold">{user?.name}</span>
                  <p className="text-xs font-normal text-slate-400">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/profile" className="cursor-pointer"><UserIcon className="w-4 h-4 mr-2" /> Profile</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/settings" className="cursor-pointer"><Cog className="w-4 h-4 mr-2" /> Settings</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/billing" className="cursor-pointer"><CreditCard className="w-4 h-4 mr-2" /> Billing</Link></DropdownMenuItem>
                {["master", "admin"].includes(user?.role) && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer font-semibold text-purple-600 dark:text-purple-400">
                      <ShieldCheck className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" /> Admin Portal
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/signin" className="cursor-pointer text-blue-600 dark:text-blue-400">
                    <UserIcon className="w-4 h-4 mr-2" /> Switch Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doSignout} data-testid="menu-logout" className="cursor-pointer text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto" data-testid="main-content">
          {isTrialExpired && loc.pathname !== "/billing" && !loc.pathname.startsWith("/billing") && loc.pathname !== "/profile" ? (
            <TrialExpiredLockView />
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      <AIAssistant open={aiOpen} onOpenChange={setAiOpen} />
      <AppGuideTour isOpen={showTour} onClose={() => setShowTour(false)} />
      <PlatformGuideChatbot
        isOpen={helpChatbotOpen}
        onClose={() => setHelpChatbotOpen(false)}
        onStartTour={() => setShowTour(true)}
      />
    </div>
  );
}
